import { AbstractModel } from '@/models/abstractModel';
import { BetSelection, Bookmaker, EventFeedType, EventPeriod, Jollywhere, OrderOddsType } from '@/constants';
import { FeedLiveItem } from '@/models/feedLive';
import { AppError } from '@/errors/appError';
import { FeedPregameItem } from '@/models/feedPregame';
import { useEventStore } from '@/stores/event';
import { formatOdds } from '@/helpers';
import { useUserStore } from '@/stores/user';
import { useAppStore } from '@/stores/app';
import { orderService } from '@/services';
import { v4 as uuidv4 } from 'uuid';
import { MaxStake, PriceNum } from '@/types';
import { OrderItem } from '@/models/order';
import { BetItem } from '@/models/bet';
import { usePlatform } from '@/composables/usePlatform';

export class BettingData extends AbstractModel {
    public id!: number;
    public isCreatedFromFeed: boolean = false;
    public customEventId!: string;
    public leagueName!: string;
    public iso3!: string | null;
    public teamHomeName!: string;
    public teamAwayName!: string;
    public leagueId!: number;
    public teamHomeId!: number;
    public teamAwayId!: number;
    public teamHomeLogo!: string | null;
    public teamAwayLogo!: string | null;
    public team!: string;
    public correctScore!: string | null;
    public eventStartedAt!: string;
    public eventFeedType!: EventFeedType;
    public periodTypeId!: EventPeriod;
    public betTypeId!: number;
    public betSelectionId!: BetSelection;
    public isActive!: boolean;
    public uuid!: number;
    public scoreHome!: number | null;
    public scoreAway!: number | null;
    public time!: string | null;
    public cornersHome!: number | null;
    public cornersAway!: number | null;

    public handicapKey!: string | null;
    public oddsMaxKey!: string | null;

    public bookmakerId!: Bookmaker | null;
    public priceList!: [number, number][] | null;
    public price!: string | null;
    public handicap!: string | null;
    public jollywhere!: Jollywhere | null;

    public extraOdds?: Record<Bookmaker, Record<PriceNum, MaxStake>>;
    // public extraOddsLinesCount?: number;

    public oddsMaxStake?: Record<Bookmaker, MaxStake>;

    public odds: string = '';
    public stake: number | null = null;
    public timeout: number | null = 300;
    public isTopUps: 0 | 1 = 0;

    private highlightClassIndex: Record<string, number> = markRaw({});
    private previousPrices: Record<string, string | null> = markRaw({});

    public orderPlaced: OrderItem | null = null;

    public eventScoreForBet!: (bet: BetItem) => { home: number | null; away: number | null };

    constructor() {
        super();
    }

    public get scoreFull(): string {
        if (this.scoreHome === null) {
            return '';
        }
        return `${this.scoreHome}-${this.scoreAway}`;
    }

    public get corners(): string {
        if (this.cornersHome === null) {
            return '';
        }
        return `${this.cornersHome}-${this.cornersAway}`;
    }

    updatePriceDataFromFeed(data: FeedLiveItem | FeedPregameItem): void {
        this.setPreviousPrices();

        switch (this.betSelectionId) {
            // 1X2 markets
            case BetSelection.OneXTwoHome:
                this.bookmakerId = data.wldHomeBookmakerId.value;
                this.price = data.wldHomePrice.value;
                this.priceList = data.wldHomePriceList;

                break;
            case BetSelection.OneXTwoDraw:
                this.bookmakerId = data.wldDrawBookmakerId.value;
                this.price = data.wldDrawPrice.value;
                this.priceList = data.wldDrawPriceList;

                break;
            case BetSelection.OneXTwoAway:
                this.bookmakerId = data.wldAwayBookmakerId.value;
                this.price = data.wldAwayPrice.value;
                this.priceList = data.wldAwayPriceList;

                break;

            // Handicap markets
            case BetSelection.HandicapHome:
                this.bookmakerId = data.handicapHomeBookmakerId.value;
                this.price = data.handicapHomePrice.value;
                this.priceList = data.handicapHomePriceList;

                break;
            case BetSelection.HandicapAway:
                this.bookmakerId = data.handicapAwayBookmakerId.value;
                this.price = data.handicapAwayPrice.value;
                this.priceList = data.handicapAwayPriceList;

                break;

            // Goal markets
            case BetSelection.GoalOver:
                this.bookmakerId = data.goalOverBookmakerId.value;
                this.price = data.goalOverPrice.value;
                this.priceList = data.goalOverPriceList;

                break;
            case BetSelection.GoalUnder:
                this.bookmakerId = data.goalUnderBookmakerId.value;
                this.price = data.goalUnderPrice.value;
                this.priceList = data.goalUnderPriceList;

                break;

            // BTS markets
            case BetSelection.BtsYes:
                this.bookmakerId = data.btsYesBookmakerId.value;
                this.price = data.btsYesPrice.value;
                this.priceList = data.btsYesPriceList;

                break;
            case BetSelection.BtsNo:
                this.bookmakerId = data.btsNoBookmakerId.value;
                this.price = data.btsNoPrice.value;
                this.priceList = data.btsNoPriceList;

                break;

            // Double Chance markets
            case BetSelection.Dc1X:
                this.bookmakerId = data.dc1XBookmakerId.value;
                this.price = data.dc1XPrice.value;
                this.priceList = data.dc1XPriceList;

                break;
            case BetSelection.Dc12:
                this.bookmakerId = data.dc12BookmakerId.value;
                this.price = data.dc12Price.value;
                this.priceList = data.dc12PriceList;

                break;
            case BetSelection.DcX2:
                this.bookmakerId = data.dcX2BookmakerId.value;
                this.price = data.dcX2Price.value;
                this.priceList = data.dcX2PriceList;

                break;

            default:
                throw new AppError(`Unknown betSelectionId ${this.betSelectionId}`);
        }
    }

    updatePriceDataFromEvent() {
        const eventStore = useEventStore();
        //this.jollywhere = null;

        this.setPreviousPrices();

        // Determine which market data to use based on periodTypeId
        const market1x2 = this.periodTypeId === EventPeriod.FirstHalf ? eventStore.market1x21H : eventStore.market1x2FT;
        const marketGoal = this.periodTypeId === EventPeriod.FirstHalf ? eventStore.marketGoal1H : eventStore.marketGoalFT;
        const marketHandicap = this.periodTypeId === EventPeriod.FirstHalf ? eventStore.marketHandicap1H : eventStore.marketHandicapFT;
        const marketBts = this.periodTypeId === EventPeriod.FirstHalf ? eventStore.marketBts1H : eventStore.marketBtsFT;
        const marketDc = this.periodTypeId === EventPeriod.FirstHalf ? eventStore.marketDc1H : eventStore.marketDcFT;
        const marketCs = this.periodTypeId === EventPeriod.FirstHalf ? eventStore.marketCs1H : eventStore.marketCsFT;
        const marketCornersGoal = this.periodTypeId === EventPeriod.FirstHalf ? eventStore.marketCornersGoal1H : eventStore.marketCornersGoalFT;
        const marketCornersHandicap = this.periodTypeId === EventPeriod.FirstHalf ? eventStore.marketCornersHandicap1H : eventStore.marketCornersHandicapFT;

        switch (this.betSelectionId) {
            // 1X2 markets
            case BetSelection.OneXTwoHome:
                this.jollywhere = null;
                if (market1x2) {
                    this.bookmakerId = market1x2.homeBookmakerId;
                    this.price = market1x2.homePrice;
                    this.priceList = market1x2.homePriceList;
                } else {
                    this.bookmakerId = null;
                    this.price = null;
                    this.priceList = null;
                }
                break;
            case BetSelection.OneXTwoDraw:
                this.jollywhere = null;
                if (market1x2) {
                    this.bookmakerId = market1x2.drawBookmakerId;
                    this.price = market1x2.drawPrice;
                    this.priceList = market1x2.drawPriceList;
                } else {
                    this.bookmakerId = null;
                    this.price = null;
                    this.priceList = null;
                }
                break;
            case BetSelection.OneXTwoAway:
                this.jollywhere = null;
                if (market1x2) {
                    this.bookmakerId = market1x2.awayBookmakerId;
                    this.price = market1x2.awayPrice;
                    this.priceList = market1x2.awayPriceList;
                } else {
                    this.bookmakerId = null;
                    this.price = null;
                    this.priceList = null;
                }
                break;

            // Handicap markets
            case BetSelection.HandicapHome:
                if (marketHandicap && marketHandicap.length > 0) {
                    const handicap = marketHandicap.find((item) => item.handicap === this.handicap && item.jollywhere === this.jollywhere);

                    this.bookmakerId = handicap?.homeBookmakerId ?? null;
                    this.price = handicap?.homePrice ?? null;
                    this.priceList = handicap?.homePriceList ?? null;
                } else {
                    this.bookmakerId = null;
                    this.price = null;
                    this.priceList = null;
                }
                break;
            case BetSelection.HandicapAway:
                if (marketHandicap && marketHandicap.length > 0) {
                    const handicap = marketHandicap.find((item) => item.handicap === this.handicap && item.jollywhere === this.jollywhere);

                    this.bookmakerId = handicap?.awayBookmakerId ?? null;
                    this.price = handicap?.awayPrice ?? null;
                    this.priceList = handicap?.awayPriceList ?? null;
                } else {
                    this.bookmakerId = null;
                    this.price = null;
                    this.priceList = null;
                }
                break;

            // Goal markets
            case BetSelection.GoalOver:
                this.jollywhere = null;
                if (marketGoal && marketGoal.length > 0) {
                    const goal = marketGoal.find((item) => item.handicap === this.handicap);

                    this.bookmakerId = goal?.overBookmakerId ?? null;
                    this.price = goal?.overPrice ?? null;
                    this.priceList = goal?.overPriceList ?? null;
                } else {
                    this.bookmakerId = null;
                    this.price = null;
                    this.priceList = null;
                }
                break;
            case BetSelection.GoalUnder:
                this.jollywhere = null;
                if (marketGoal && marketGoal.length > 0) {
                    const goal = marketGoal.find((item) => item.handicap === this.handicap);

                    this.bookmakerId = goal?.underBookmakerId ?? null;
                    this.price = goal?.underPrice ?? null;
                    this.priceList = goal?.underPriceList ?? null;
                } else {
                    this.bookmakerId = null;
                    this.price = null;
                    this.priceList = null;
                }
                break;

            // BTS markets
            case BetSelection.BtsYes:
                this.jollywhere = null;
                if (marketBts) {
                    this.bookmakerId = marketBts.yesBookmakerId;
                    this.price = marketBts.yesPrice;
                    this.priceList = marketBts.yesPriceList;
                } else {
                    this.bookmakerId = null;
                    this.price = null;
                    this.priceList = null;
                }
                break;
            case BetSelection.BtsNo:
                this.jollywhere = null;
                if (marketBts) {
                    this.bookmakerId = marketBts.noBookmakerId;
                    this.price = marketBts.noPrice;
                    this.priceList = marketBts.noPriceList;
                } else {
                    this.bookmakerId = null;
                    this.price = null;
                    this.priceList = null;
                }
                break;

            // Double Chance markets
            case BetSelection.Dc1X:
                this.jollywhere = null;
                if (marketDc) {
                    this.bookmakerId = marketDc.dc1XBookmakerId;
                    this.price = marketDc.dc1XPrice;
                    this.priceList = marketDc.dc1XPriceList;
                } else {
                    this.bookmakerId = null;
                    this.price = null;
                    this.priceList = null;
                }
                break;
            case BetSelection.Dc12:
                this.jollywhere = null;
                if (marketDc) {
                    this.bookmakerId = marketDc.dc12BookmakerId;
                    this.price = marketDc.dc12Price;
                    this.priceList = marketDc.dc12PriceList;
                }
                break;
            case BetSelection.DcX2:
                this.jollywhere = null;
                if (marketDc) {
                    this.bookmakerId = marketDc.dcX2BookmakerId;
                    this.price = marketDc.dcX2Price;
                    this.priceList = marketDc.dcX2PriceList;
                } else {
                    this.bookmakerId = null;
                    this.price = null;
                    this.priceList = null;
                }
                break;

            // Correct Score market
            case BetSelection.Cs:
                this.jollywhere = null;
                if (marketCs && marketCs.length > 0) {
                    // Find the correct score item that matches this.correctScore
                    const csItem = marketCs.find((item) => item.score === this.correctScore);
                    if (csItem) {
                        this.bookmakerId = csItem.bookmakerId;
                        this.price = csItem.price;
                        this.priceList = csItem.priceList;
                    }
                } else {
                    this.bookmakerId = null;
                    this.price = null;
                    this.priceList = null;
                }
                break;

            // Corners Handicap markets
            case BetSelection.CornersHandicapHome:
                if (marketCornersHandicap && marketCornersHandicap.length > 0) {
                    const cornersHandicap = marketCornersHandicap.find((item) => item.handicap === this.handicap && item.jollywhere === this.jollywhere);

                    this.bookmakerId = cornersHandicap?.homeBookmakerId ?? null;
                    this.price = cornersHandicap?.homePrice ?? null;
                    this.priceList = cornersHandicap?.homePriceList ?? null;
                } else {
                    this.bookmakerId = null;
                    this.price = null;
                    this.priceList = null;
                }
                break;
            case BetSelection.CornersHandicapAway:
                if (marketCornersHandicap && marketCornersHandicap.length > 0) {
                    const cornersHandicap = marketCornersHandicap.find((item) => item.handicap === this.handicap && item.jollywhere === this.jollywhere);

                    this.bookmakerId = cornersHandicap?.awayBookmakerId ?? null;
                    this.price = cornersHandicap?.awayPrice ?? null;
                    this.priceList = cornersHandicap?.awayPriceList ?? null;
                } else {
                    this.bookmakerId = null;
                    this.price = null;
                    this.priceList = null;
                }
                break;

            // Corners Goal markets
            case BetSelection.CornersGoalOver:
                this.jollywhere = null;
                if (marketCornersGoal && marketCornersGoal.length > 0) {
                    const cornersGoal = marketCornersGoal.find((item) => item.handicap === this.handicap);

                    this.bookmakerId = cornersGoal?.overBookmakerId ?? null;
                    this.price = cornersGoal?.overPrice ?? null;
                    this.priceList = cornersGoal?.overPriceList ?? null;
                } else {
                    this.bookmakerId = null;
                    this.price = null;
                    this.priceList = null;
                }
                break;
            case BetSelection.CornersGoalUnder:
                this.jollywhere = null;
                if (marketCornersGoal && marketCornersGoal.length > 0) {
                    const cornersGoal = marketCornersGoal.find((item) => item.handicap === this.handicap);

                    this.bookmakerId = cornersGoal?.underBookmakerId ?? null;
                    this.price = cornersGoal?.underPrice ?? null;
                    this.priceList = cornersGoal?.underPriceList ?? null;
                } else {
                    this.bookmakerId = null;
                    this.price = null;
                    this.priceList = null;
                }
                break;

            default:
                throw new AppError(`Unknown betSelectionId ${this.betSelectionId}`);
        }

        this.priceList = this.priceList ? this.priceList.map((data: any) => [data[0], data[2]]) : null;
    }

    public get siteOdds() {
        let siteOdds: Record<string, string> = {};

        if (this.priceList) {
            (this.priceList ?? []).forEach(function (site) {
                // var idx = '2' in site ? 2 : 1;
                siteOdds[site[0]] = formatOdds(site[1]);
            });
        }
        return siteOdds;
    }

    public siteMinStake(site: number): number {
        const appStore = useAppStore();

        //PINNA changed it, no need to re-calculate minStake
        // if (userStore.currency.id === Currency.Brl && site === Bookmaker.Pinna) {
        //     if (Number(this.odds) < 1.9) {
        //         minStakeVal = Math.floor((5.4 / (Number(this.odds) - 1) / 70) * 100);
        //     }
        // }
        return appStore.betOrderMinStakes.filter((minStake) => minStake[0] === site)[0][1];
    }

    public get availableMaxStake() {
        if (this.oddsMaxStake) {
            const self = this;
            let availableMaxStakeVal = 0;

            (this.priceList ?? []).forEach(function (data: [Bookmaker, number]) {
                const price = formatOdds(data[1]);
                if (Number(price) >= Number(self.odds)) {
                    availableMaxStakeVal += self.oddsMaxStake![data[0]] ?? 0;
                }
                if (self.extraOdds && self.extraOdds[data[0]]) {
                    Object.entries(self.extraOdds[data[0]]).forEach(([priceExtra, availableStake]) => {
                        if (Number(priceExtra) >= Number(self.odds)) {
                            availableMaxStakeVal += availableStake as number;
                        }
                    });
                }
            });
            return availableMaxStakeVal;
        }
        return null;
    }

    public get bestOdds(): string {
        return formatOdds(this.price!);
    }

    public get bestMaxStake(): number {
        let bestMaxStake = 0;
        let bestOddsSiteId = this.bookmakerId!;
        if (this.oddsMaxStake && this.oddsMaxStake[bestOddsSiteId]) {
            bestMaxStake = this.oddsMaxStake[bestOddsSiteId];
        }
        return bestMaxStake;
    }

    public get medianOdds() {
        let oddsList = !this.isActive ? [] : this.priceList;

        if (!oddsList) return null;

        let medianOddsVal = null;
        if (oddsList && this.stake) {
            const userStore = useUserStore();
            oddsList = oddsList.filter((oddsArr) => userStore.settings.bet_order_site_ids_allowed.indexOf(oddsArr[0]) !== -1 && this.siteMinStake(oddsArr[0]) <= Number(this.stake));
        }
        if (oddsList.length === 1) {
            medianOddsVal = Math.floor(oddsList[oddsList.length - 1][1] * 1000) / 1000;
        } else if (oddsList.length > 1) {
            let median = Math.round(oddsList.length / 2);
            if (oddsList.length % 2 === 0) {
                let odds1 = oddsList[median - 1][1];
                let odds2 = oddsList[median][1];
                medianOddsVal = Math.floor(((odds1 + odds2) / 2) * 1000) / 1000;
            } else {
                medianOddsVal = Math.floor(oddsList[median - 1][1] * 1000) / 1000;
            }
        }
        return formatOdds(Number(medianOddsVal));
    }

    public get medianMaxStake() {
        if (this.oddsMaxStake) {
            const self = this;
            let medianMaxStakeVal = 0;
            (this.priceList ?? []).forEach(function (data: [Bookmaker, number]) {
                const price = formatOdds(data[1]);
                if (parseFloat(price) >= Number(self.medianOdds)) {
                    medianMaxStakeVal += self.oddsMaxStake![data[0]] ?? 0;
                }
                if (self.extraOdds && self.extraOdds[data[0]]) {
                    Object.entries(self.extraOdds[data[0]]).forEach(([oddsKey, val]) => {
                        if (parseFloat(oddsKey) >= Number(self.medianOdds)) {
                            medianMaxStakeVal += val as number;
                        }
                    });
                }
            });
            return medianMaxStakeVal;
        }
        return null;
    }

    public get totalOdds() {
        let totalOdds = 0;

        if (this.priceList) {
            totalOdds = this.priceList[this.priceList.length - 1][1];
        }
        return formatOdds(totalOdds);
    }

    public get totalMaxStake() {
        if (this.oddsMaxStake) {
            const self = this;
            let totalMaxStakeVal = 0;

            (this.priceList ?? []).forEach(function (data: [Bookmaker, number]) {
                totalMaxStakeVal += self.oddsMaxStake![data[0]] ?? 0;
                if (self.extraOdds && self.extraOdds[data[0]]) {
                    Object.entries(self.extraOdds[data[0]]).forEach(([, val]) => {
                        totalMaxStakeVal += val as number;
                    });
                }
            });
            return totalMaxStakeVal;
        }
        return null;
    }

    public isLowSiteMinStake(site: number): boolean {
        return this.siteMinStake(site) > Number(this.stake);
    }

    public get selectionLabel() {
        const labelMap = {
            [BetSelection.HandicapHome]: 'betDialog.handicap',
            [BetSelection.HandicapAway]: 'betDialog.handicap',
            [BetSelection.GoalOver]: 'betDialog.goal',
            [BetSelection.GoalUnder]: 'betDialog.goal',
            [BetSelection.CornersHandicapHome]: 'betDialog.cornersAsianHandicap',
            [BetSelection.CornersHandicapAway]: 'betDialog.cornersAsianHandicap',
            [BetSelection.CornersGoalOver]: 'betDialog.cornersGoal',
            [BetSelection.CornersGoalUnder]: 'betDialog.cornersGoal',
            [BetSelection.OneXTwoHome]: 'betDialog.1X2',
            [BetSelection.OneXTwoDraw]: 'betDialog.1X2',
            [BetSelection.OneXTwoAway]: 'betDialog.1X2',
            [BetSelection.BtsYes]: 'betDialog.btts',
            [BetSelection.BtsNo]: 'betDialog.btts',
            [BetSelection.Dc1X]: 'betDialog.doubleChance',
            [BetSelection.Dc12]: 'betDialog.doubleChance',
            [BetSelection.DcX2]: 'betDialog.doubleChance',
            [BetSelection.Cs]: 'betDialog.correctScore',
        };
        return this.t(labelMap[this.betSelectionId] || '');
    }

    public get returns() {
        return this.stake && this.odds ? (Number(this.stake) * Number(this.odds)).toFixed(2) : '';
    }

    public setPreviousPrices() {
        if (this.priceList) {
            this.priceList.forEach((price) => {
                this.previousPrices[price[0]] = formatOdds(price[1]);
            });
        }
    }
    public async setPreviousExtraPrices() {
        if (this.extraOdds) {
            Object.entries(this.extraOdds).forEach(([bookmaker, extraPrices]) => {
                Object.keys(extraPrices).forEach((price, idx) => {
                    this.previousPrices[`${bookmaker}_${idx}`] = price;
                });
            });
        }
    }

    public getHighlightClass(bookmakerId: Bookmaker, price: string, extraPriceIdx: number | null = null): string {
        const key = extraPriceIdx !== null ? `${bookmakerId}_${extraPriceIdx}` : bookmakerId;
        const hlClassIndex = (this.highlightClassIndex[key] ?? 2) === 2 ? 1 : 2;
        this.highlightClassIndex[key] = hlClassIndex;
        const previousPrice = this.previousPrices[key];

        if (previousPrice !== null) {
            if (price > previousPrice) {
                return `highlightGreen${hlClassIndex}`;
            } else if (price < previousPrice) {
                return `highlightRed${hlClassIndex}`;
            }
        }

        return '';
    }

    private adjustHandicap(): string | null {
        if (this.handicap === null) return null;

        let handicap: string = this.handicap;
        const shouldNegate =
            (Number(handicap) !== 0 && this.betSelectionId === BetSelection.HandicapHome && this.jollywhere === Jollywhere.Home) ||
            (Number(handicap) !== 0 && this.betSelectionId === BetSelection.HandicapAway && this.jollywhere === Jollywhere.Away) ||
            (Number(handicap) !== 0 && this.betSelectionId === BetSelection.CornersHandicapHome && this.jollywhere === Jollywhere.Home) ||
            (Number(handicap) !== 0 && this.betSelectionId === BetSelection.CornersHandicapAway && this.jollywhere === Jollywhere.Away);
        if (shouldNegate) {
            handicap = '-' + handicap;
        }
        return handicap;
    }

    private buildAvailablePrices(): Record<number, number> {
        const result: Record<number, number> = {};
        (this.priceList ?? []).forEach((item) => {
            result[item[0]] = item[1];
        });
        return result;
    }

    public placeOrder(sites: { value: number[] }, isNoScoreAndTimeAccepted: boolean = false) {
        const isLive = this.eventFeedType === EventFeedType.Live;
        const handicap = this.adjustHandicap();
        const platform = usePlatform();

        const sitesArr = sites.value.filter((site: number) => !this.isLowSiteMinStake(site));
        const availablePrices = this.buildAvailablePrices();

        const order = {
            available_prices: JSON.stringify(availablePrices),
            custom_event_id: this.customEventId,
            date_game: this.eventStartedAt,
            custom_league_id: this.leagueId,
            custom_team_home_id: this.teamHomeId,
            custom_team_away_id: this.teamAwayId,
            //todo we have betSelectionId so betTypeId can be removed
            bet_type_id: this.betTypeId,
            bet_selection_id: this.betSelectionId,
            period_type_id: this.periodTypeId,
            is_top_ups: this.isTopUps,
            score: isLive ? this.scoreFull : null,
            correct_score: this.betSelectionId === BetSelection.Cs ? this.correctScore : null,
            corners: isLive ? this.corners : null,
            handicap: handicap,
            time: isLive ? this.time : null,
            is_live: isLive,
            market_id: this.eventFeedType,
            odds: this.odds,
            stake: this.stake,
            timeout: this.timeout === null ? 0 : this.timeout === 0 ? null : this.timeout, // 0 value means no timeout(nothing in input), null means Kick Off (0 option in input)
            jollywhere: this.jollywhere,
            uuid: uuidv4(),
            is_no_score_and_time_accepted: isLive && isNoScoreAndTimeAccepted,
        };

        if (platform.isAggregator()) {
            (order as any).site_ids = sitesArr.sort((a, b) => a - b).join();
        }

        return orderService.create(order);
    }

    public get isOrderPlaced(): boolean {
        return this.orderPlaced != null;
    }

    public initTimeout() {
        const userStore = useUserStore();

        if (this.eventFeedType === EventFeedType.Live) {
            this.timeout = userStore.settings.bet_order_live_timeout_default;
        } else if (this.eventFeedType === EventFeedType.Today || this.eventFeedType === EventFeedType.Early) {
            this.timeout = userStore.settings.bet_order_dball_timeout_default;
        }
    }

    public initStake() {
        const userStore = useUserStore();

        if (userStore.settings.bet_order_stake_default) {
            this.stake = userStore.settings.bet_order_stake_default;
        }
    }

    public initOdds() {
        const userStore = useUserStore();

        const platform = usePlatform();

        if (platform.isBookmaker()) {
            this.odds = formatOdds(this.price!);
            return;
        }

        let oddsList = !this.isActive ? [] : (this.priceList ?? []);
        if (oddsList && this.stake) {
            oddsList = oddsList.filter((oddsArr) => userStore.settings.bet_order_site_ids_allowed.indexOf(oddsArr[0]) !== -1);
        }

        if (userStore.settings.bet_order_odds_default_type_id === OrderOddsType.Best) {
            if (oddsList.length !== 0) {
                let maxArr: any = [];
                oddsList.forEach((oddsArr) => maxArr.push(oddsArr[1]));
                this.odds = Math.max(...maxArr).toString();
            }
        } else if (userStore.settings.bet_order_odds_default_type_id === OrderOddsType.Lowest) {
            if (oddsList.length !== 0) {
                this.odds = (Math.floor(oddsList[oddsList.length - 1][1] * 1000) / 1000).toString();
            }
        } else if (userStore.settings.bet_order_odds_default_type_id === OrderOddsType.Median) {
            const median = this.medianOdds;
            if (median != null) {
                this.odds = String(median);
            }
        }
        this.odds = formatOdds(this.odds);
    }
}
